// 📄 Report.js
import React, { useState, useEffect } from 'react';
import { Table, Input, Button, Col, Card, CardHeader, CardBody, CardFooter, Label, Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';
import AdminReportDetails from '../features/Reports/AdminReportDetails';
import CreateReportDetails from '../features/Reports/CreateAdminReport';
import reportService from 'services/report.service';

function Report() {
    const [reports, setReports] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState('list'); // 'list' hoặc 'detail'
    const [dataProps, setDataProps] = useState(null);

    useEffect(() => {
        loadReports(currentPage);
    }, [currentPage]);

    /**
     * 📥 Load báo cáo theo phân trang
     */
    function loadReports(page) {
        setLoading(true);
        reportService.getAllReportsByAdmin(page, 10).then((result) => {
            setReports(result.data);
            setTotalPages(result.totalPages);
        }).catch((error) => {
            Swal.fire('Lỗi!', 'Không thể tải báo cáo', 'error');
        }).finally(() => {
            setLoading(false);
        });
    }

    /**
     * 🛠️ Rút gọn chuỗi
     */
    function truncateText(text, maxLength) {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    }

    /** 🖱️ Xem chi tiết báo cáo */
    function toDetailView(report) {
        setPage('detail');
        setDataProps(report);
    }

    /** ➕ Tạo báo cáo */
    function toCreateReport() {
        setPage('create');
    }

    /** ⬅️ Quay lại danh sách báo cáo */
    function back() {
        setPage('list');
        loadReports(currentPage);
    }

    /**
     * 🕐 Format thời gian báo cáo
     */
    function formatReportTime(dateStr) {
        if (!dateStr) return { dateFormatted: '—', timeStr: '', relative: '' };
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now - d;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        const timeStr = d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
        const dateFormatted = d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });

        let relative = '';
        if (diffMins < 1) relative = 'Vừa xong';
        else if (diffMins < 60) relative = `${diffMins} phút trước`;
        else if (diffHours < 24) relative = `${diffHours} giờ trước`;
        else if (diffDays < 7) relative = `${diffDays} ngày trước`;

        return { dateFormatted, timeStr, relative };
    }

    /**
     * 📊 Status badge
     */
    function renderStatusBadge(status) {
        const statusMap = {
            'Pending': { color: '#dc3545', bg: 'rgba(220,53,69,0.1)', label: 'Chờ xử lý' },
            'InProgress': { color: '#ffc107', bg: 'rgba(255,193,7,0.1)', label: 'Đang xử lý' },
            'Resolved': { color: '#198754', bg: 'rgba(25,135,84,0.1)', label: 'Đã xong' },
            'Ignored': { color: '#6c757d', bg: 'rgba(108,117,125,0.1)', label: 'Bỏ qua' }
        };
        const s = statusMap[status] || { color: '#666', bg: '#f0f0f0', label: status };
        return (
            <span style={{
                display: 'inline-block',
                padding: '4px 10px',
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                color: s.color,
                backgroundColor: s.bg
            }}>
                {s.label}
            </span>
        );
    }

    // Shared styles
    const cardStyle = {
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: 'none',
        overflow: 'hidden',
        margin: '0 auto'
    };

    const thStyle = {
        border: 'none',
        fontSize: '14px',
        color: '#8898aa',
        fontWeight: '600',
        textTransform: 'uppercase'
    };

    /**
     * 📊 Hiển thị bảng báo cáo
     */
    function renderTable() {
        const filtered = reports.filter((report) => {
            if (search === '') return true;
            const q = search.toLowerCase();
            return (
                report.reportBy?.fullname?.toLowerCase().includes(q) ||
                report.toolName?.toLowerCase().includes(q) ||
                report.errorData?.toLowerCase().includes(q) ||
                report.status?.toLowerCase().includes(q) ||
                report.stackTrace?.toLowerCase().includes(q)
            );
        });

        return (
            <Table style={{ marginTop: "10px", marginBottom: 0 }}>
                <thead>
                    <tr style={{ borderBottom: '2px solid #f0f0f0' }}>
                        <th style={thStyle}>Thời gian</th>
                        <th style={thStyle}>Người báo lỗi</th>
                        <th style={thStyle}>Người xử lý</th>
                        <th style={thStyle}>Phần mềm</th>
                        <th style={thStyle}>Tiêu đề</th>
                        <th style={thStyle}>Dữ liệu lỗi</th>
                        <th style={thStyle}>Ghi chú</th>
                        <th style={thStyle}>Trạng thái</th>
                        <th style={{ ...thStyle, width: '80px' }}>Xóa</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((report, index) => {
                        const time = formatReportTime(report.reportedAt || report.createdAt);
                        return (
                            <tr
                                key={index}
                                onClick={() => toDetailView(report)}
                                style={{
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f5f5f5',
                                    transition: 'background-color 0.2s ease'
                                }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <td style={{ border: 'none', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                    <div style={{ fontSize: '15px', fontWeight: '500', color: '#2c3e50' }}>
                                        {time.dateFormatted}
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#8898aa' }}>
                                        {time.timeStr}
                                        {time.relative && (
                                            <span style={{ marginLeft: '6px', color: '#51cbce', fontWeight: '500' }}>
                                                • {time.relative}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>
                                    {report.reportBy?.fullname}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f' }}>
                                    {report.handledBy?.fullname || <span style={{ color: '#c8d6e5' }}>—</span>}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        padding: '3px 8px',
                                        borderRadius: '20px',
                                        backgroundColor: 'rgba(81,203,206,0.1)',
                                        color: '#2c3e50'
                                    }}>
                                        {report.toolName}
                                    </span>
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f', maxWidth: '180px' }}>
                                    {truncateText(report.stackTrace, 40)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f', maxWidth: '180px' }}>
                                    {truncateText(report.errorData, 40)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle', fontSize: '15px', color: '#525f7f', maxWidth: '140px' }}>
                                    {truncateText(report.note, 30)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                    {renderStatusBadge(report.status)}
                                </td>
                                <td style={{ border: 'none', verticalAlign: 'middle' }}>
                                    <Button
                                        color="danger"
                                        size="sm"
                                        style={{ borderRadius: '20px', fontSize: '13px', padding: '4px 12px', whiteSpace: 'nowrap' }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            Swal.fire({
                                                title: 'Xác nhận xóa báo cáo?',
                                                icon: 'warning',
                                                showCancelButton: true,
                                                confirmButtonText: 'Xóa',
                                                cancelButtonText: 'Hủy',
                                            }).then((result) => {
                                                if (result.isConfirmed) {
                                                    reportService.deleteReport(report.id).then(() => {
                                                        Swal.fire('Thành công!', 'Xóa báo cáo thành công', 'success');
                                                        loadReports(currentPage);
                                                    }).catch((error) => {
                                                        Swal.fire('Lỗi!', `Không thể xóa báo cáo <br><br> ${error}`, 'error');
                                                    });
                                                }
                                            });
                                        }}
                                    >
                                        <i className="fas fa-times" style={{ marginRight: '4px' }} /> Xóa
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    }

    return (
        <div className="content">
            <style>{`.content input.admin-report-search::placeholder { color: rgba(255,255,255,0.6) !important; }`}</style>

            {page === 'detail' ? (
                <>
                    <Button color="info" onClick={back} style={{ borderRadius: '20px', marginBottom: '10px' }}>
                        <i className="fas fa-chevron-left" style={{ marginRight: '6px' }} /> Quay lại
                    </Button>
                    <AdminReportDetails data={dataProps} onBack={back} />
                </>
            ) : page === 'create' ? (
                <>
                    <Button color="info" onClick={back} style={{ borderRadius: '20px', marginBottom: '10px' }}>
                        <i className="fas fa-chevron-left" style={{ marginRight: '6px' }} /> Quay lại
                    </Button>
                    <CreateReportDetails onBack={back} />
                </>
            ) : (
                <Card style={cardStyle}>
                    <CardHeader style={{
                        background: 'linear-gradient(135deg, #e67e22 0%, #d35400 100%)',
                        padding: '20px 24px',
                        borderBottom: 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <i className="fas fa-file-alt" style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
                            <div style={{ lineHeight: '1.3' }}>
                                <Label style={{ fontSize: '18px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>
                                    Báo lỗi nội bộ (Admin)
                                </Label>
                                <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)' }}>
                                    {reports.length > 0 ? `${reports.length} báo cáo trên trang này` : 'Đang tải...'}
                                </span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ position: 'relative' }}>
                                <i className="fas fa-search" style={{
                                    position: 'absolute',
                                    left: '12px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: 'rgba(255,255,255,0.6)',
                                    fontSize: '15px',
                                    zIndex: 1
                                }}></i>
                                <Input
                                    className="admin-report-search"
                                    style={{
                                        height: "38px",
                                        borderRadius: '20px',
                                        border: 'none',
                                        fontSize: '14px',
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        backdropFilter: 'blur(4px)',
                                        paddingLeft: '36px',
                                        width: '220px'
                                    }}
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    placeholder="Tìm kiếm..."
                                />
                            </div>
                            <Button
                                onClick={toCreateReport}
                                style={{
                                    background: 'rgba(255,255,255,0.2)',
                                    border: 'none',
                                    color: '#fff',
                                    borderRadius: '20px',
                                    padding: '8px 16px',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <i className="fas fa-plus" style={{ marginRight: '6px' }}></i> Tạo báo lỗi
                            </Button>
                        </div>
                    </CardHeader>
                    <CardBody style={{ padding: '16px 24px', overflowY: 'auto', overflowX: 'hidden' }}>
                        {loading ? (
                            <div className="text-center py-4">
                                <Spinner color="warning" />
                            </div>
                        ) : renderTable()}
                    </CardBody>
                    <CardFooter style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <Button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            style={{ borderRadius: '20px', fontSize: '14px', padding: '6px 16px' }}
                        >
                            <i className="fas fa-chevron-left" style={{ marginRight: '6px' }} /> Trang trước
                        </Button>
                        <span style={{ fontSize: '14px', color: '#8898aa' }}>Trang {currentPage} / {totalPages}</span>
                        <Button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            style={{ borderRadius: '20px', fontSize: '14px', padding: '6px 16px' }}
                        >
                            Trang sau <i className="fas fa-chevron-right" style={{ marginLeft: '6px' }} />
                        </Button>
                    </CardFooter>
                </Card>
            )}
        </div>
    );
}

export default Report;
